// TanStack Query 기반 데이터 hook + mutation. 모든 ajax 진입점은 여기.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiDelete, apiGet, apiPost, apiPut } from './api'
import type { DocumentDto, FolderDto, GroupDto, ProjectDto } from './types'

// ── Groups ──
export function useGroups() {
  return useQuery({
    queryKey: ['groups'],
    queryFn: () => apiGet<GroupDto[]>('/api/groups'),
  })
}

/** Groups 홈 화면 전용 — 그룹 + 그룹별 프로젝트를 한 응답에 fetch (N+1 회피, 깜빡임 제거). */
export function useGroupsWithProjects() {
  return useQuery({
    queryKey: ['groups', { include: 'projects' }],
    queryFn: () => apiGet<GroupDto[]>('/api/groups?include=projects'),
  })
}

export function useCreateGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: { name: string; description?: string }) =>
      apiPost<GroupDto>('/api/groups', req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  })
}

export function useUpdateGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, req }: { uuid: string; req: { name: string; description?: string } }) =>
      apiPut<GroupDto>(`/api/groups/${uuid}`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  })
}

export function useDeleteGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => apiDelete(`/api/groups/${uuid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  })
}

export function useReorderGroups() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuids: string[]) =>
      apiPut<GroupDto[]>('/api/groups/reorder', { uuids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  })
}

// ── Projects ──
export function useGroupProjects(groupUuid: string | undefined) {
  return useQuery({
    queryKey: ['groups', groupUuid, 'projects'],
    queryFn: () => apiGet<ProjectDto[]>(`/api/groups/${groupUuid}/projects`),
    enabled: !!groupUuid,
  })
}

export function useProjectByKey(projectKey: string | undefined) {
  return useQuery({
    queryKey: ['projects', 'key', projectKey],
    queryFn: () => apiGet<ProjectDto>(`/api/projects/by-key/${projectKey}`),
    enabled: !!projectKey,
  })
}

export function useProject(projectUuid: string | undefined) {
  return useQuery({
    queryKey: ['projects', projectUuid],
    queryFn: () => apiGet<ProjectDto>(`/api/projects/${projectUuid}`),
    enabled: !!projectUuid,
  })
}

export function useCreateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupUuid, req }: { groupUuid: string; req: { name: string; description?: string; publicReadable?: boolean } }) =>
      apiPost<ProjectDto>(`/api/groups/${groupUuid}/projects`, req),
    // ['groups', ...] 모두 invalidate — useGroupsWithProjects 와 useGroupProjects 둘 다 갱신
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  })
}

export function useDeleteProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => apiDelete(`/api/projects/${uuid}`),
    onSuccess: () => qc.invalidateQueries(),
  })
}

export function useUpdateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, req }: { uuid: string; req: { name?: string; description?: string; publicReadable?: boolean } }) =>
      apiPut<ProjectDto>(`/api/projects/${uuid}`, req),
    onSuccess: () => qc.invalidateQueries(),
  })
}

export function useReorderProjects() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupUuid, uuids }: { groupUuid: string; uuids: string[] }) =>
      apiPut<ProjectDto[]>(`/api/groups/${groupUuid}/projects/reorder`, { uuids }),
    // ['groups', ...] prefix 매칭 — useGroupsWithProjects 와 useGroupProjects 둘 다 갱신
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  })
}

export function useMoveProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, targetGroupUuid }: { uuid: string; targetGroupUuid: string }) =>
      apiPut<ProjectDto>(`/api/projects/${uuid}/move`, { targetGroupUuid }),
    onSuccess: () => qc.invalidateQueries(),
  })
}

// ── Folders ──
export function useFolderTree(projectUuid: string | undefined) {
  return useQuery({
    queryKey: ['projects', projectUuid, 'folders'],
    queryFn: () => apiGet<FolderDto[]>(`/api/projects/${projectUuid}/folders`),
    enabled: !!projectUuid,
  })
}

export function useCreateFolder(projectUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: { name: string; parentUuid: string | null }) =>
      apiPost<FolderDto>(`/api/projects/${projectUuid}/folders`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', projectUuid, 'folders'] }),
  })
}

export function useUpdateFolder(projectUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, name }: { uuid: string; name: string }) =>
      apiPut<FolderDto>(`/api/folders/${uuid}`, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', projectUuid, 'folders'] }),
  })
}

export function useDeleteFolder(projectUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => apiDelete(`/api/folders/${uuid}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects', projectUuid, 'folders'] })
      qc.invalidateQueries({ queryKey: ['projects', projectUuid, 'documents'] })
    },
  })
}

export function useMoveFolder(_projectUuid?: string) {
  const qc = useQueryClient()
  return useMutation({
    // parentUuid=null + targetProjectUuid → 다른 프로젝트의 root 직속으로 이동
    mutationFn: ({ uuid, parentUuid, targetProjectUuid }: {
      uuid: string; parentUuid: string | null; targetProjectUuid?: string | null
    }) =>
      apiPut<FolderDto>(`/api/folders/${uuid}/move`, { parentUuid, targetProjectUuid }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  })
}

export function useReorderFolders(projectUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ parentUuid, uuids }: { parentUuid: string | null; uuids: string[] }) =>
      apiPost<FolderDto[]>('/api/folders/reorder', { projectUuid, parentUuid, uuids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', projectUuid, 'folders'] }),
  })
}

// ── Document mutations ──
export function useUpdateDocument(projectUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, req }: { uuid: string; req: { title?: string; content?: string } }) =>
      apiPut<DocumentDto>(`/api/documents/${uuid}`, req),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['projects', projectUuid, 'documents'] })
      qc.invalidateQueries({ queryKey: ['documents', vars.uuid] })
    },
  })
}

export function useDeleteDocument(projectUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => apiDelete(`/api/documents/${uuid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', projectUuid, 'documents'] }),
  })
}

export function useMoveDocument(_projectUuid?: string) {
  const qc = useQueryClient()
  return useMutation({
    // folderUuid=null + targetProjectUuid → 다른 프로젝트의 root 직속으로 이동
    mutationFn: ({ uuid, folderUuid, targetProjectUuid }: {
      uuid: string; folderUuid: string | null; targetProjectUuid?: string | null
    }) =>
      apiPut<DocumentDto>(`/api/documents/${uuid}/move`, { folderUuid, targetProjectUuid }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  })
}

export function useReorderDocuments(projectUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ folderUuid, uuids }: { folderUuid: string; uuids: string[] }) =>
      apiPost<DocumentDto[]>('/api/documents/reorder', { folderUuid, uuids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', projectUuid, 'documents'] }),
  })
}

// ── Documents ──
export function useDocumentsByProject(projectUuid: string | undefined, folderUuid?: string) {
  const url = folderUuid
    ? `/api/projects/${projectUuid}/documents?folderUuid=${folderUuid}`
    : `/api/projects/${projectUuid}/documents`
  return useQuery({
    queryKey: ['projects', projectUuid, 'documents', folderUuid ?? null],
    queryFn: () => apiGet<DocumentDto[]>(url),
    enabled: !!projectUuid,
  })
}

export function useDocument(uuid: string | undefined) {
  return useQuery({
    queryKey: ['documents', uuid],
    queryFn: () => apiGet<DocumentDto>(`/api/documents/${uuid}`),
    enabled: !!uuid,
  })
}
